import React, { useState, useEffect, useMemo } from 'react'
import { X, Banknote, TrendingUp, TrendingDown, Minus, CreditCard, CheckCircle2, ChevronDown, ChevronUp, ShieldAlert } from 'lucide-react'

// ── Colombian denominations ────────────────────────────────────────────────────
const BILLS = [
  { value: 100_000, label: '$100.000' },
  { value:  50_000, label: '$50.000'  },
  { value:  20_000, label: '$20.000'  },
  { value:  10_000, label: '$10.000'  },
  { value:   5_000, label: '$5.000'   },
  { value:   2_000, label: '$2.000'   },
  { value:   1_000, label: '$1.000'   },
];
const COINS = [
  { value: 500, label: '$500' },
  { value: 200, label: '$200' },
  { value: 100, label: '$100' },
  { value:  50, label: '$50'  },
];

function fmt(n: number) {
  return '$ ' + Math.round(n).toLocaleString('es-CO');
}

interface PaymentBreakdown {
  method: string;
  amount: number;
}

interface CloseModalProps {
  visible: boolean;
  onClose: () => void;
  onConfirm: (finalAmount: number, observations: string, cashCount: Record<string, number>, paymentBreakdown: PaymentBreakdown[]) => void;
  loading?: boolean;
  expectedCash?: number;           // del summary del backend
  paymentBreakdown?: PaymentBreakdown[];
  isAdmin?: boolean;               // Si es true, ve montos esperados y diferencia. Si es false, arqueo ciego.
}

export const CloseModal: React.FC<CloseModalProps> = ({
  visible,
  onClose,
  onConfirm,
  loading,
  expectedCash = 0,
  paymentBreakdown = [],
  isAdmin = false,
}) => {
  const [step, setStep] = useState<'count' | 'review'>('count');
  const [showCoins, setShowCoins] = useState(false);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [observations, setObservations] = useState('');

  useEffect(() => {
    if (visible) {
      setStep('count');
      setCounts({});
      setObservations('');
      setShowCoins(false);
    }
  }, [visible]);

  const setCount = (value: number, qty: number) => {
    setCounts(prev => ({ ...prev, [String(value)]: Math.max(0, qty) }));
  };

  const countedCash = useMemo(() =>
    Object.entries(counts).reduce((sum, [denom, qty]) => sum + Number(denom) * qty, 0),
    [counts]
  );

  const difference = countedCash - expectedCash;
  const hasCounted = countedCash > 0;

  function handleConfirm() {
    onConfirm(
      hasCounted ? countedCash : expectedCash,
      observations,
      counts,
      paymentBreakdown,
    );
  }

  if (!visible) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className="bg-white dark:bg-neutral-800 rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center justify-between border-b border-gray-100 dark:border-neutral-700 px-5 py-4 sticky top-0 bg-white dark:bg-neutral-800 z-10">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Banknote size={20} className="text-primary" />
            Cerrar Caja — Arqueo
          </h2>
          <button onClick={onClose} className="flex h-8 w-8 items-center justify-center rounded-full hover:bg-gray-100 dark:hover:bg-neutral-700">
            <X size={16} />
          </button>
        </div>

        <div className="p-5 space-y-5">

          {/* ── Step tabs ── */}
          <div className="flex rounded-xl border border-gray-200 dark:border-neutral-600 overflow-hidden text-sm font-semibold">
            {(['count', 'review'] as const).map(s => (
              <button
                key={s}
                type="button"
                onClick={() => setStep(s)}
                className={`flex-1 py-2.5 transition ${step === s ? 'bg-primary text-white' : 'text-gray-500 hover:bg-gray-50 dark:hover:bg-neutral-700'}`}
              >
                {s === 'count' ? '1. Contar billetes' : '2. Revisar y cerrar'}
              </button>
            ))}
          </div>

          {/* ── STEP 1: Bill counter ── */}
          {step === 'count' && (
            <div className="space-y-3">
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Ingresa cuántos billetes y monedas tienes físicamente en caja.
              </p>

              {/* Bills */}
              <div className="space-y-2">
                <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Billetes</h3>
                {BILLS.map(({ value, label }) => {
                  const qty = counts[String(value)] ?? 0;
                  return (
                    <div key={value} className="flex items-center gap-3">
                      <span className="w-20 text-sm font-semibold text-gray-700 dark:text-gray-300 shrink-0">{label}</span>
                      <div className="flex items-center gap-2 flex-1">
                        <button type="button" onClick={() => setCount(value, qty - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 font-bold text-lg text-gray-600">−</button>
                        <input
                          type="number" min={0}
                          value={qty || ''}
                          onChange={e => setCount(value, parseInt(e.target.value) || 0)}
                          placeholder="0"
                          className="w-16 rounded-lg border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-2 py-1.5 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/50"
                        />
                        <button type="button" onClick={() => setCount(value, qty + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 font-bold text-lg text-gray-600">+</button>
                        <span className="flex-1 text-right text-sm text-gray-500">{qty > 0 ? fmt(value * qty) : ''}</span>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Coins (collapsible) */}
              <button
                type="button"
                onClick={() => setShowCoins(v => !v)}
                className="flex items-center gap-1 text-xs font-semibold text-primary hover:underline"
              >
                {showCoins ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
                {showCoins ? 'Ocultar monedas' : 'Agregar monedas'}
              </button>

              {showCoins && (
                <div className="space-y-2">
                  <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500">Monedas</h3>
                  {COINS.map(({ value, label }) => {
                    const qty = counts[String(value)] ?? 0;
                    return (
                      <div key={value} className="flex items-center gap-3">
                        <span className="w-20 text-sm font-semibold text-gray-700 dark:text-gray-300 shrink-0">{label}</span>
                        <div className="flex items-center gap-2 flex-1">
                          <button type="button" onClick={() => setCount(value, qty - 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 font-bold text-lg text-gray-600">−</button>
                          <input
                            type="number" min={0}
                            value={qty || ''}
                            onChange={e => setCount(value, parseInt(e.target.value) || 0)}
                            placeholder="0"
                            className="w-16 rounded-lg border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-2 py-1.5 text-center text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-primary/50"
                          />
                          <button type="button" onClick={() => setCount(value, qty + 1)} className="flex h-8 w-8 items-center justify-center rounded-lg border border-gray-200 hover:bg-gray-50 font-bold text-lg text-gray-600">+</button>
                          <span className="flex-1 text-right text-sm text-gray-500">{qty > 0 ? fmt(value * qty) : ''}</span>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Notice for non-admins (Blind cash close) */}
              {!isAdmin && (
                <div className="rounded-xl bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800/40 p-3 flex items-start gap-2.5 text-xs text-amber-900 dark:text-amber-200">
                  <ShieldAlert size={16} className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <span className="font-bold">Arqueo Ciego Activo:</span>
                    <p className="mt-0.5 text-amber-800/90 dark:text-amber-300/80">
                      Ingresa el conteo físico de los billetes y monedas que entregas en tu turno. El cuadre contra el sistema será auditado por el Administrador.
                    </p>
                  </div>
                </div>
              )}

              {/* Counted total */}
              <div className="rounded-xl bg-gray-50 dark:bg-neutral-700/50 border border-gray-100 dark:border-neutral-600 p-4">
                {isAdmin ? (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-gray-600 dark:text-gray-400">Efectivo esperado</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">{fmt(expectedCash)}</span>
                    </div>
                    <div className="flex justify-between items-center text-base font-bold mt-2 pt-2 border-t border-gray-200 dark:border-neutral-600">
                      <span className="text-gray-900 dark:text-white">Total contado</span>
                      <span className={hasCounted ? 'text-primary' : 'text-gray-400'}>{hasCounted ? fmt(countedCash) : '—'}</span>
                    </div>
                  </>
                ) : (
                  <div className="flex justify-between items-center text-base font-bold">
                    <span className="text-gray-900 dark:text-white">Total físico contado</span>
                    <span className={hasCounted ? 'text-primary' : 'text-gray-400'}>{hasCounted ? fmt(countedCash) : '—'}</span>
                  </div>
                )}
              </div>

              <button
                type="button"
                onClick={() => setStep('review')}
                className="w-full rounded-xl bg-primary py-3 text-sm font-bold text-white hover:opacity-90 transition"
              >
                Continuar a Confirmación →
              </button>
            </div>
          )}

          {/* ── STEP 2: Review ── */}
          {step === 'review' && (
            <div className="space-y-4">

              {isAdmin ? (
                <>
                  {/* Difference box (ADMIN ONLY) */}
                  {hasCounted && (
                    <div className={`rounded-xl p-4 border-2 ${
                      Math.abs(difference) < 1000 ? 'border-emerald-400 bg-emerald-50 dark:bg-emerald-900/20'
                      : difference > 0 ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20'
                      : 'border-red-400 bg-red-50 dark:bg-red-900/20'
                    }`}>
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-xs font-bold uppercase tracking-wide text-gray-500">Diferencia del arqueo</p>
                          <p className={`text-2xl font-black mt-0.5 ${
                            Math.abs(difference) < 1000 ? 'text-emerald-600'
                            : difference > 0 ? 'text-blue-600' : 'text-red-600'
                          }`}>
                            {difference >= 0 ? '+' : ''}{fmt(difference)}
                          </p>
                        </div>
                        <div className="text-3xl">
                          {Math.abs(difference) < 1000 ? <CheckCircle2 size={40} className="text-emerald-500" />
                          : difference > 0 ? <TrendingUp size={40} className="text-blue-500" />
                          : <TrendingDown size={40} className="text-red-500" />}
                        </div>
                      </div>
                      <p className="text-xs mt-2 text-gray-500">
                        {Math.abs(difference) < 1000 ? 'Caja cuadrada ✓'
                        : difference > 0 ? `Sobrante de ${fmt(difference)}`
                        : `Faltante de ${fmt(Math.abs(difference))}`}
                      </p>
                    </div>
                  )}

                  {/* Summary table */}
                  <div className="rounded-xl border border-gray-100 dark:border-neutral-700 divide-y divide-gray-50 dark:divide-neutral-700 text-sm">
                    <Row label="Monto esperado" value={fmt(expectedCash)} />
                    {hasCounted && <Row label="Total contado" value={fmt(countedCash)} bold />}
                  </div>

                  {/* Payment breakdown */}
                  {paymentBreakdown.length > 0 && (
                    <div>
                      <h3 className="text-xs font-bold uppercase tracking-wide text-gray-500 mb-2 flex items-center gap-1">
                        <CreditCard size={12} /> Ventas por método de pago
                      </h3>
                      <div className="rounded-xl border border-gray-100 dark:border-neutral-700 divide-y divide-gray-50 dark:divide-neutral-700 text-sm">
                        {paymentBreakdown.map(p => (
                          <Row key={p.method} label={p.method} value={fmt(p.amount)} />
                        ))}
                      </div>
                    </div>
                  )}
                </>
              ) : (
                /* NON-ADMIN: Declaración Física de Cierre Ciego */
                <div className="space-y-3">
                  <div className="rounded-xl p-4 border border-blue-200 dark:border-blue-900/40 bg-blue-50/60 dark:bg-blue-950/20">
                    <div className="flex items-center gap-2 text-blue-700 dark:text-blue-300 font-bold text-sm">
                      <ShieldAlert size={18} />
                      <span>Declaración de Efectivo Físico</span>
                    </div>
                    <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                      Estás por confirmar el cierre de turno. El dinero entregado será verificado por el Administrador al auditar la caja.
                    </p>
                    <div className="pt-3 mt-3 border-t border-blue-200 dark:border-blue-900/40 flex justify-between items-center">
                      <span className="text-sm font-semibold text-gray-700 dark:text-gray-300">Total físico declarado:</span>
                      <span className="text-2xl font-black text-blue-700 dark:text-blue-300">{fmt(countedCash)}</span>
                    </div>
                  </div>

                  <div className="rounded-xl border border-gray-100 dark:border-neutral-700 divide-y divide-gray-50 dark:divide-neutral-700 text-xs text-gray-500 p-3">
                    <div className="flex justify-between py-1">
                      <span>Denominaciones contadas:</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {Object.values(counts).filter(q => q > 0).length} denominaciones
                      </span>
                    </div>
                    <div className="flex justify-between py-1">
                      <span>Total unidades (billetes/monedas):</span>
                      <span className="font-semibold text-gray-700 dark:text-gray-300">
                        {Object.values(counts).reduce((a, b) => a + b, 0)} unidades
                      </span>
                    </div>
                  </div>
                </div>
              )}

              {/* Observations */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 dark:text-gray-400 mb-1">
                  Observaciones del cierre
                </label>
                <textarea
                  value={observations}
                  onChange={e => setObservations(e.target.value)}
                  rows={3}
                  placeholder="Ej: Turno entregado a satisfacción, sin novedades..."
                  className="w-full rounded-xl border border-gray-200 dark:border-neutral-600 bg-white dark:bg-neutral-700 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 resize-none"
                />
              </div>

              {/* Actions */}
              <div className="flex gap-3">
                <button type="button" onClick={() => setStep('count')} className="flex-1 rounded-xl border border-gray-200 py-2.5 text-sm font-medium hover:bg-gray-50 transition">
                  ← Volver al conteo
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={loading}
                  className="flex-1 rounded-xl bg-red-600 py-2.5 text-sm font-bold text-white hover:bg-red-500 disabled:opacity-50 transition"
                >
                  {loading ? 'Cerrando...' : (isAdmin ? '🔒 Cerrar Caja' : '🔒 Confirmar y Cerrar Turno')}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div className={`flex justify-between px-4 py-2.5 ${bold ? 'font-bold text-gray-900 dark:text-white' : 'text-gray-600 dark:text-gray-400'}`}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}
